import { useQueries } from '@tanstack/react-query'
import { AxiosResponse } from 'axios'

import { queryKeys } from '@/constants/queryKeys'
import {
  getBankEligibility,
  getBankEligibilityOnboardingState,
  getBusinessPaymentLimits,
} from '@/domains/Business/api'
import { Card as CardType } from '@/domains/Business/features/Cards/types'
import { useBusinessRole, useFeature } from '@/domains/Business/hooks'
import {
  BankEligibility,
  BankEligibilityOnboardingState,
  Feature,
  PaymentLimit,
} from '@/domains/Business/types'
import { Card, MotionDiv } from '@/shared/ui'

import { AccountType } from '../../Accounts/types'
import { getAllCards } from '../../Cards'

import { CardsBanner } from './CardsBanner'
import { HomeEURCBanner } from './HomeEURCBanner'
import { LimitBanner } from './LimitBanner'

export const HomeBanners = () => {
  const { isAdmin } = useBusinessRole()

  const { isEnabled, isPending: isCardFeaturePending } = useFeature(
    Feature.CARDS,
  )

  const { isEnabled: isEURCWalletEnabled, isPending: isEURCWalletPending } =
    useFeature(Feature.EURC_WALLET)

  const [
    paymentLimitsQuery,
    cardsQuery,
    bankEligibilityQuery,
    bankAccountOnboardingStateQuery,
  ] = useQueries({
    queries: [
      {
        queryKey: [queryKeys.getBusinessPaymentLimits],
        queryFn: getBusinessPaymentLimits,
        select: (data: AxiosResponse<PaymentLimit>) => data.data,
        enabled: isAdmin,
      },
      {
        queryKey: [queryKeys.getAllCards],
        queryFn: () => getAllCards(),
        select: (data: AxiosResponse<CardType[]>) => data?.data,
        enabled: isAdmin && isEnabled,
      },
      {
        queryKey: [queryKeys.getBankEligibility],
        queryFn: () => getBankEligibility(),
        select: (data: AxiosResponse<BankEligibility>) => data.data,
        enabled: isAdmin && isEURCWalletEnabled,
      },
      {
        queryKey: [queryKeys.getBankEligibilityOnboardingState],
        queryFn: () => getBankEligibilityOnboardingState(AccountType.EU_LOCAL),
        select: (data: AxiosResponse<BankEligibilityOnboardingState>) =>
          data.data,
        enabled: isAdmin && isEURCWalletEnabled,
        retry: 1,
      },
    ],
  })

  const hasLimitBanner = paymentLimitsQuery.data?.periodTransferLimit
  const hasCardBanner = isEnabled && cardsQuery.data
  const hasBankEligibilityBanner =
    isEURCWalletEnabled && !bankAccountOnboardingStateQuery.data?.state

  const isFetching =
    cardsQuery.isFetching ||
    paymentLimitsQuery.isFetching ||
    bankEligibilityQuery.isFetching ||
    isCardFeaturePending ||
    isEURCWalletPending

  if (!hasLimitBanner || isFetching) {
    return null
  }

  return (
    <MotionDiv>
      <Card size="medium" className="grid grid-cols-1 gap-8 md:grid-cols-2">
        {hasBankEligibilityBanner ? null : hasLimitBanner ? (
          <LimitBanner limits={paymentLimitsQuery.data?.periodTransferLimit} />
        ) : null}
        {hasCardBanner && <CardsBanner cards={cardsQuery.data} />}
        {hasBankEligibilityBanner && (
          <HomeEURCBanner bankEligibility={bankEligibilityQuery.data} />
        )}
      </Card>
    </MotionDiv>
  )
}
